import { Link } from "react-router-dom";
import { Palette, Nfc, Compass, MessageSquareText, Truck, BadgeCheck } from "lucide-react";
import Seo from "../components/Seo";
import PageHero from "../components/PageHero";
import { CtaWhatsApp } from "../components/Cta";
import { Reveal } from "../components/motion";
import NfcDemo from "../components/NfcDemo";
import { DELIVERY_DELAY, RESPONSE_TIME } from "../config/site";

const STEPS = [
  {
    n: "01",
    icon: Palette,
    t: "Personnalisez",
    d: "Depuis le configurateur ou directement sur WhatsApp, définissez votre design : logo, couleurs, nom, slogan, et le contenu de votre page digitale (avis Google, réseaux, menu, contact…). Nous vous envoyons un aperçu pour validation avant toute production.",
  },
  {
    n: "02",
    icon: Nfc,
    t: "Approchez",
    d: "Votre client approche simplement son smartphone de la carte — le NFC fait le reste. Aucune application à installer, aucun compte à créer, aucun réglage. iPhone et Android, récents ou non.",
  },
  {
    n: "03",
    icon: Compass,
    t: "Découvrez",
    d: "Votre page digitale s'ouvre dans le navigateur : vos liens professionnels réunis, à votre image. Le client laisse un avis, s'abonne, appelle ou se laisse guider — en quelques secondes.",
  },
];

const AFTER_ORDER = [
  { icon: MessageSquareText, t: "Commande sur WhatsApp", d: `Vous nous écrivez, nous échangeons sur votre projet. ${RESPONSE_TIME}.` },
  { icon: Palette, t: "Design validé ensemble", d: "Nous préparons votre carte et votre page digitale, vous validez l'aperçu. Rien n'est produit sans votre accord." },
  { icon: Truck, t: "Production & livraison", d: "Production et livraison sous 7 à 10 jours ouvrés après validation de la commande." },
  { icon: BadgeCheck, t: "Activation immédiate", d: "À réception, la carte fonctionne immédiatement. Et si vos liens évoluent, votre page digitale évolue avec eux." },
];

export default function CommentCaMarche() {
  return (
    <>
      <Seo
        title="Comment ça marche"
        description="Personnalisez, approchez, découvrez : le fonctionnement de la carte digitale Noveocard, de la commande WhatsApp à la livraison en 7 à 10 jours ouvrés."
        path="/comment-ca-marche"
      />

      <PageHero
        dark
        eyebrow="Comment ça marche"
        titleLines={["Trois gestes.", "Zéro friction."]}
        description="De la personnalisation à la découverte de votre page digitale : voici exactement ce qui se passe entre votre commande et le premier geste de vos clients."
      />

      {/* Démo animée */}
      <section className="grain relative overflow-hidden bg-nuit-950 py-16 text-white sm:py-20">
        <div className="container-nv">
          <Reveal>
            <NfcDemo />
          </Reveal>
        </div>
      </section>

      {/* Les 3 étapes */}
      <section className="bg-white py-20 sm:py-28">
        <div className="container-nv">
          {STEPS.map((s, i) => (
            <Reveal key={s.n}>
              <div
                className={`grid items-center gap-8 border-t border-nuit-900/10 py-14 lg:grid-cols-[auto_1fr_auto] lg:gap-16 ${
                  i === 0 ? "border-t-0 pt-0" : ""
                }`}
              >
                <span className="font-display text-7xl font-bold leading-none text-brume-300 sm:text-9xl" aria-hidden="true">
                  {s.n}
                </span>
                <div className="max-w-2xl">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-electric/10">
                    <s.icon className="h-5 w-5 text-electric" />
                  </span>
                  <h2 className="mt-5 font-display text-3xl font-bold tracking-tight text-nuit-900 sm:text-4xl">
                    {s.t}
                  </h2>
                  <p className="mt-4 text-base leading-relaxed text-steel sm:text-lg">{s.d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Après la commande */}
      <section className="bg-brume py-20 sm:py-24">
        <div className="container-nv">
          <Reveal className="max-w-2xl">
            <p className="eyebrow">Et côté commande ?</p>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-nuit-900 sm:text-4xl">
              Tout se passe sur WhatsApp. Simplement.
            </h2>
            <p className="mt-4 text-steel">
              Pas de panier, pas de compte client, pas de tunnel de paiement : une conversation
              humaine, du premier message à la livraison.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {AFTER_ORDER.map((s, i) => (
              <Reveal key={s.t} delay={i * 0.08}>
                <article className="h-full rounded-3xl border border-nuit-900/10 bg-white p-6">
                  <s.icon className="h-6 w-6 text-electric" />
                  <h3 className="mt-4 font-display text-base font-bold text-nuit-900">{s.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-steel">{s.d}</p>
                </article>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12 flex flex-col items-center gap-3 text-center">
            <CtaWhatsApp size="lg" showResponseTime testid="hiw-cta-whatsapp" />
            <p className="text-sm text-steel">
              Envie de visualiser d'abord ?{" "}
              <Link to="/personnalisation" className="font-semibold text-electric underline-offset-2 hover:underline" data-testid="hiw-configurator-link">
                Essayez le configurateur
              </Link>
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
